import React from "react";
import { useNavigate } from "react-router-dom";
import { CachedAvatar } from "@/shared/components/ui/cached-avatar";
import { UpcomingAppointmentsWidgetProps } from "../types";
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
/**
 * Ícones por tipo de compromisso
 */
const appointmentIcons = {
  meeting: 'map-pin',
  call: 'phone',
  video: 'video',
};

/**
 * Labels por tipo de compromisso
 */
const appointmentTypeLabels: Record<string, string> = {
  meeting: 'Reunião',
  call: 'Ligação',
  video: 'Vídeo',
};

/**
 * Cores por status — tokens semânticos (F62/F63): verde = confirmado,
 * azul = agendado, âmbar = pendente (atenção).
 */
const statusColors: Record<string, { dot: string; text: string }> = {
  confirmed: { dot: 'var(--theme-success-color)', text: 'Confirmado' },
  scheduled: { dot: 'var(--theme-info-color)', text: 'Agendado' },
  pending: { dot: 'var(--theme-warning-color)', text: 'Pendente' },
};

/**
 * Formata o horário relativo do compromisso
 */
const formatTime = (datetime: Date | string): string => {
  const date = typeof datetime === 'string' ? new Date(datetime) : datetime;
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

/**
 * Formata o tempo relativo (em X min, em X h)
 */
const formatRelative = (datetime: Date | string): string => {
  const now = new Date();
  const date = typeof datetime === 'string' ? new Date(datetime) : datetime;
  const diffMs = date.getTime() - now.getTime();

  if (diffMs < 0) return 'agora';

  const diffMin = Math.floor(diffMs / (1000 * 60));
  if (diffMin < 60) return `em ${diffMin}min`;

  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `em ${diffH}h`;

  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

/**
 * Cor do indicador de urgência baseado na proximidade (tokens — F63)
 */
const getUrgencyColor = (datetime: Date | string): string => {
  const now = new Date();
  const date = typeof datetime === 'string' ? new Date(datetime) : datetime;
  const diffH = (date.getTime() - now.getTime()) / (1000 * 60 * 60);

  if (diffH <= 1) return 'var(--theme-error-color)';   // Vermelho - muito próximo
  if (diffH <= 3) return 'var(--theme-warning-color)'; // Âmbar - próximo
  return 'var(--theme-info-color)';                    // Azul - distante
};

/**
 * Widget de próximos compromissos do dashboard
 *
 * Design premium com mini-cards por compromisso,
 * indicador de tempo com cor de urgência,
 * e status dots compactos.
 */
export const UpcomingAppointmentsWidget: React.FC<UpcomingAppointmentsWidgetProps> = ({
  appointments,
  maxItems = 4,
  onViewAll,
}) => {
  const navigate = useNavigate();

  const handleAppointmentClick = (datetime: Date | string) => {
    const date = typeof datetime === 'string' ? new Date(datetime) : datetime;
    const dateParam = date.toISOString().split('T')[0];
    navigate(`/calendar?date=${dateParam}&view=day`);
  };

  const visibleAppointments = appointments.slice(0, maxItems);

  return (
    <div
      className="h-full rounded-lg border flex flex-col overflow-hidden"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-3.5 border-b"
        style={{ borderColor: 'var(--theme-card-border)' }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3
              className="text-sm font-bold tracking-tight"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Próximos Compromissos
            </h3>
            <p
              className="text-xs mt-0.5"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Sua agenda · próximos 30 dias
            </p>
          </div>
          <Icon
            name="calendar"
            className="w-4 h-4 flex-shrink-0 mt-0.5"
            style={{ color: 'var(--theme-muted-text)' }}
          />
        </div>
      </div>

      {/* Appointment List */}
      <div className="flex-1 px-3 py-2 space-y-1.5 overflow-y-auto">
        {visibleAppointments.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-8">
            <Icon name="calendar" className="h-10 w-10 mb-3"
              style={{ color: 'var(--theme-muted-text)', opacity: 0.3 }} />
            <p
              className="text-sm font-medium mb-1"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Nenhum compromisso
            </p>
            <p
              className="text-xs text-center"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Você não tem compromissos agendados para os próximos 30 dias
            </p>
          </div>
        ) : visibleAppointments.map((appointment) => {
          const typeIconName = appointmentIcons[appointment.type as keyof typeof appointmentIcons];
          const typeLabel = appointmentTypeLabels[appointment.type] ?? '';
          const status = statusColors[appointment.status] || statusColors.scheduled;
          const urgencyColor = getUrgencyColor(appointment.datetime);

          return (
            <div
              key={appointment.id}
              data-testid={`appointment-row-${appointment.id}`}
              className="flex items-center gap-3 pl-2 pr-3 py-2.5 rounded-lg cursor-pointer transition-colors"
              style={{ backgroundColor: 'transparent' }}
              onClick={() => handleAppointmentClick(appointment.datetime)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {/* Rail de urgência */}
              <div
                className="w-[3px] self-stretch rounded-full flex-shrink-0"
                style={{ backgroundColor: urgencyColor }}
              />

              {/* Time indicator */}
              <div className="flex-shrink-0 text-center" style={{ minWidth: '44px' }}>
                <div
                  className="text-xs font-bold"
                  style={{
                    color: 'var(--theme-card-text)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatTime(appointment.datetime)}
                </div>
                <div
                  className="text-[10px] mt-0.5"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  {formatRelative(appointment.datetime)}
                </div>
              </div>

              {/* Avatar */}
              <div className="flex-shrink-0">
                <CachedAvatar
                  src={appointment.client.avatar}
                  alt={appointment.client.name}
                  size="sm"
                />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p
                    className="text-xs font-medium truncate"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    {appointment.title}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Icon
                    name={typeIconName as IconName}
                    className="h-3 w-3 flex-shrink-0"
                    style={{ color: 'var(--theme-muted-text)' }}
                  />
                  <span
                    className="text-[11px] truncate"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    {typeLabel} · {appointment.client.name}
                  </span>
                  <span
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: status.dot }}
                  />
                  <span
                    className="text-[11px] flex-shrink-0"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    {status.text}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      {onViewAll && (
        <div
          className="px-4 py-2.5 border-t"
          style={{ borderColor: 'var(--theme-card-border)' }}
        >
          <button
            data-testid="appointments-view-all"
            className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold h-11 rounded-md transition-colors"
            style={{ color: 'var(--theme-primary)' }}
            onClick={onViewAll}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Ver toda a agenda
            <Icon name="arrow-right" className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

UpcomingAppointmentsWidget.displayName = 'UpcomingAppointmentsWidget';
